import { Text, Link, BasicButton } from "@certn/cascadia";
import { Card, mq } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CardContent } from "./CardContent";
import { EditRescreenModal } from "../../pages/person-profile/components/EditRescreenModal";
import { TagList } from "../tag-list/TagList";

interface RescreenCardProps {
  caseID: string;
  subtitle: string;
  scheduledDate: string | null;
  tags: string[];
  checks: string[];
  rescreenFrequency: string;
  refreshData: () => void;
}

export function RescreenCaseCard({
  caseID,
  subtitle,
  scheduledDate,
  tags,
  checks,
  rescreenFrequency,
  refreshData,
}: RescreenCardProps) {
  const { t } = useTranslation();
  const [isCaseExpanded, setIsCaseExpanded] = useState(false);

  const [showEditRescreenModal, setShowEditRescreenModal] = useState(false);
  return (
    <Card type="large" testID="rescreen-case-card">
      <div className={styles.caseCardHeader}>
        <div className={styles.headerFirstLine}>
          <div className={styles.headerInfoSection}>
            <Text
              className={css({ whiteSpace: "nowrap" })}
              style="global/heading-medium"
              testID="case-short-id-title"
            >
              {t("Upcoming rescreen")}
            </Text>
          </div>
          <div className={styles.headerButtonGroup}>
            <BasicButton
              size="small"
              onPress={() => setShowEditRescreenModal(true)}
              testID="edit-rescreen-button"
            >
              {t("Edit")}
            </BasicButton>
          </div>
        </div>
        <div className={styles.headerSecondLine}>
          <Text color="text-weak" testID="case-card-subtitle">
            <span>{subtitle}</span>
            <span style={{ margin: "0 var(--space-1)" }}>•</span>
            <span>
              {t("Scheduled")} {formatDate(scheduledDate, "PP")}
            </span>
          </Text>
        </div>
        {tags && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <TagList
              tags={tags}
              capacity={3}
              hasOverflowDetails
              testID="case-card"
            />
          </div>
        )}

        {!isCaseExpanded && (
          <div style={{ marginTop: "var(--space-2)" }}>
            <Link
              textStyle="global/body-secondary"
              textColor="text-base"
              onPress={() => setIsCaseExpanded(!isCaseExpanded)}
              aria-controls="case-details"
              aria-expanded={false}
              aria-label={t("Case Details")}
              testID="show-more-text"
            >
              {t("Show more")}
            </Link>
          </div>
        )}
      </div>
      {isCaseExpanded && (
        <CardContent
          caseId={caseID}
          checks={checks}
          linkedCases={[]}
          setIsCaseExpanded={setIsCaseExpanded}
          isCaseExpanded={isCaseExpanded}
          canCancelRescreen
          rescreenSchedule={rescreenFrequency}
          refreshData={refreshData}
        />
      )}
      <EditRescreenModal
        onClose={() => setShowEditRescreenModal(false)}
        isOpen={showEditRescreenModal}
        caseId={caseID}
        refreshData={refreshData}
        selectedKey={rescreenFrequency}
        scheduledDate={scheduledDate}
      />
    </Card>
  );
}
const styles = {
  caseCard: css({
    display: "flex",
    flexDirection: "column",
  }),
  caseCardHeader: css({
    display: "flex",
    flexDirection: "column",
    padding: "var(--space-4)",
  }),
  headerInfoSection: css(
    {
      display: "flex",
      gap: "var(--space-2)",
      marginRight: "var(--space-1)",
    },
    mq({
      flexDirection: ["column-reverse", "row"],
      alignItems: ["normal", "center"],
      flexWrap: ["nowrap", "wrap-reverse"],
    })
  ),
  headerButtonGroup: css({
    display: "flex",
    flex: "row",
    maxHeight: "32px",
    gap: "var(--space-2)",
    whiteSpace: "nowrap",
  }),
  headerSecondLine: css({
    display: "flex",
  }),
  headerFirstLine: css({
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  }),
};
